import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import IconChevronDown from '../icons/IconChevronDown';
import type { MenuItem } from '../../features/menu/types';
import { useFilteredMenu } from '../../features/menu/useFilteredMenu';
import { navigateToVueRoute } from '../../utils/navigationHandler';

// IMPORTANT: when toggling between `translate-y-0` / `translate-y-full`
// (and `opacity-0` / `opacity-100`), apply EXACTLY ONE of each pair via
// a ternary — never both. Tailwind utilities resolve by source order in
// the compiled CSS, NOT by the order they appear in the className string,
// so writing both `translate-y-full translate-y-0` leaves the sheet
// stuck off-screen because `translate-y-full` is defined later. Same
// trap for `pointer-events-none` vs `pointer-events-auto`. Encode the
// state-dependent properties on the conditional class only.
// Height of the fixed `<BottomTabBar>` (58px content + iOS home-indicator
// safe-area inset). The backdrop and sheet both stop at the TOP of the
// bar so the bottom navigation stays visible and tappable while a
// submenu is open — see the user's UX request: "I want to display the
// mobile main menu always."
// As a Tailwind arbitrary value. CSS requires whitespace around calc()'s
// `+`, and Tailwind spells a space as `_` inside arbitrary values — the
// bare `58px+env(…)` form is invalid CSS and silently drops the offset.
const TABBAR_BOTTOM_CLASS = 'bottom-[calc(58px_+_env(safe-area-inset-bottom))]';

const BACKDROP_CLASS =
  `fixed left-0 right-0 top-0 z-[90] md:hidden ` +
  `bg-black/45 backdrop-blur-[1px] ` +
  `transition-opacity duration-200 ease-out`;

const BACKDROP_OPEN_STATE = 'opacity-100 pointer-events-auto';
const BACKDROP_CLOSED_STATE = 'opacity-0 pointer-events-none';

const SHEET_CLASS =
  `fixed left-0 right-0 z-[91] md:hidden ` +
  `flex flex-col ` +
  // Sheet height: cap at 70% viewport so the user always sees the
  // backdrop strip at the top — that's the "tap outside to close"
  // affordance. `max-h` instead of `h` so short menus stay compact.
  // Trimmed from 78% → 70% because the bottom bar now eats ~60px and
  // we don't want the top edge crowding the page chrome.
  `max-h-[70vh] min-h-[40vh] ` +
  `bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] ` +
  // Green top accent — same 3px `--color-primary` edge the other mobile
  // bottom sheets carry (order/product detail slide-ups, sort sheet).
  `rounded-t-[16px] border-t-[3px] border-solid border-t-[var(--color-primary)] ` +
  // Tall shadow above the rounded edge — gives the sheet visual lift
  // off the backdrop.
  `shadow-[0_-12px_32px_-8px_rgba(0,0,0,0.35)] ` +
  `transition-[transform,opacity] duration-300 ease-out`;

const SHEET_OPEN_STATE = 'translate-y-0 opacity-100 pointer-events-auto';
const SHEET_CLOSED_STATE = 'translate-y-full opacity-0 pointer-events-none';

const GRABBER_CLASS =
  'mx-auto mt-2 mb-1 w-10 h-[5px] rounded-full ' +
  'bg-[var(--color-border)]';

const HEADER_CLASS =
  'flex items-center justify-between px-4 py-3 ' +
  'border-b border-solid border-[var(--color-border)]';

const HEADER_TITLE_CLASS =
  'text-[15px] font-semibold leading-tight text-[var(--color-text-primary)]';

const HEADER_CLOSE_CLASS =
  'inline-flex items-center justify-center w-8 h-8 rounded-full ' +
  'bg-transparent border-0 cursor-pointer ' +
  'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] ' +
  'hover:bg-[var(--color-bg-hover)] ' +
  'transition-colors duration-fast';

const LIST_CLASS =
  'flex-1 min-h-0 overflow-y-auto px-2 py-2 ' +
  'flex flex-col gap-[2px]';

const ROW_BASE_CLASS =
  'flex items-center w-full ' +
  'px-3 py-3 rounded-md cursor-pointer ' +
  'bg-transparent border-0 text-left ' +
  'text-[14px] text-[var(--color-text-primary)] ' +
  'hover:bg-[var(--color-bg-hover)] ' +
  'transition-colors duration-fast';

const ROW_LEAF_CLASS = 'gap-3';
const ROW_PARENT_CLASS = 'gap-2 justify-between';

const ROW_LABEL_CLASS = 'flex-1 leading-snug';
const ROW_INDENT_CLASS = (depth: number) => {
  if (depth === 0) return '';
  if (depth === 1) return 'pl-6';
  return 'pl-10';
};

const CHEVRON_OPEN_CLASS =
  'transition-transform duration-200 ease-out rotate-180';
const CHEVRON_CLOSED_CLASS =
  'transition-transform duration-200 ease-out rotate-0';

// Split-row pieces for parents that carry their OWN route (Amazon,
// Baldor, CW, GFS, …): the row is a plain flex container; the LABEL
// half navigates and the CHEVRON half expands. One button doing both
// (the previous shape) navigated + closed the sheet on every tap, so
// the submenu never visibly expanded.
const SPLIT_ROW_WRAP_CLASS = 'flex items-center w-full';
const SPLIT_LABEL_BTN_CLASS =
  'flex-1 min-w-0 flex items-center px-3 py-3 rounded-md cursor-pointer ' +
  'bg-transparent border-0 text-left ' +
  'text-[14px] text-[var(--color-text-primary)] ' +
  'hover:bg-[var(--color-bg-hover)] ' +
  'transition-colors duration-fast';
// Comfortable 44px-square touch target for the expand toggle.
const SPLIT_CHEVRON_BTN_CLASS =
  'flex-none inline-flex items-center justify-center w-11 h-11 rounded-md ' +
  'bg-transparent border-0 cursor-pointer ' +
  'text-[var(--color-text-secondary)] ' +
  'hover:bg-[var(--color-bg-hover)] ' +
  'transition-colors duration-fast';

interface MobileMenuSheetProps {
  /** Top-level menu id to source the sheet contents from (e.g. `"orders"`,
   *  `"dashboard"`). Looked up against `MENU_CONFIG` after permission
   *  filtering. When the id matches no node, the sheet renders nothing —
   *  caller is expected to gate opening on a non-null `menuId`. */
  menuId: string | null;
  /** Controls whether the sheet is currently visible. The component
   *  stays mounted across open/close cycles so the slide-up CSS
   *  transition can play in both directions. */
  open: boolean;
  /** Fired by the backdrop tap, the close button, the ESC key, or after
   *  the user taps a leaf row. */
  onClose: () => void;
}

/**
 * Mobile-only slide-up submenu sheet — opens from `BottomTabBar` when
 * the user taps a section tab (Orders, Dashboard, …) that has children.
 * Renders the children of the matching `MENU_CONFIG` node as a list;
 * parent rows expand inline (accordion) so users can drill into nested
 * groups like `Sales orders → Amazon → Amazon Canada` without losing
 * the surrounding context.
 *
 * Why this lives alongside `BottomTabBar` and not inside `AsideMenu`:
 * `AsideMenu` is `hidden md:flex` (desktop only), so its panel state and
 * permission-filtered tree aren't reachable from the mobile viewport. We
 * recompute the same filtered tree here off `MENU_CONFIG`. Sourcing both
 * surfaces from the same config keeps the menu shape coherent across
 * breakpoints; the cost of recomputing the filter twice is negligible
 * (one map over the tree on permission change).
 *
 * Interactions intentionally NOT implemented (keep simple — defer until
 * users ask):
 *   • Swipe-down-to-close gesture.
 *   • Sticky breadcrumb header showing "Orders › Sales orders".
 *   • Per-row active highlight from the current route.
 */
export function MobileMenuSheet({
  menuId,
  open,
  onClose,
}: Readonly<MobileMenuSheetProps>) {
  const navigate = useNavigate();
  /** Permission-filtered menu — the shared canonical hook (also feeds
   *  AsideMenu and BottomTabBar, so every menu surface agrees). */
  const filteredMenu = useFilteredMenu();

  /** The top-level node feeding this sheet's contents. */
  const sectionNode = useMemo<MenuItem | null>(() => {
    if (!menuId) return null;
    return filteredMenu.find((item) => item.id === menuId) ?? null;
  }, [filteredMenu, menuId]);

  /** Track which parent rows are expanded — keyed by node id. Reset on
   *  every sheet (re-)open so the user always lands at a collapsed view. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (open) setExpanded(new Set());
  }, [open, menuId]);

  /** ESC key closes the sheet — standard accessibility affordance.
   *  Listener only attached while the sheet is open so we don't leak. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /** While the sheet is open, freeze body scroll so the page underneath
   *  doesn't scroll behind the backdrop. Restored on close / unmount. */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleLeafTap = useCallback(
    (leaf: MenuItem) => {
      if (!leaf.route) return;
      // Mirrors `AsideMenu.handleLeafSelect`: React routes navigate in-tab;
      // Vue routes open cross-origin in a NEW TAB, so this app is never torn
      // down by leaving for the ERP.
      if (leaf.isReactRoute) navigate(leaf.route);
      else navigateToVueRoute({ vuePath: leaf.route, openInNewTab: true });
      onClose();
    },
    [navigate, onClose],
  );

  return (
    <>
      <div
        data-testid="menu-sheet-backdrop"
        // `bottom` stops the dim layer at the top of the bottom tab bar
        // (which is `position: fixed; bottom: 0`).
        className={`${BACKDROP_CLASS} ${TABBAR_BOTTOM_CLASS} ${open ? BACKDROP_OPEN_STATE : BACKDROP_CLOSED_STATE}`}
        onClick={onClose}
        aria-hidden
      />
      <dialog
        // Native <dialog> gives the implicit dialog role. The `open`
        // attribute stays set permanently — visibility is driven by the
        // transform/opacity classes so the slide transition can play in
        // both directions (a closed <dialog> is `display: none`, which
        // would kill the animation). `m-0 p-0 w-auto h-auto` cancel the
        // UA dialog styles (margin auto, padding 1em, fit-content
        // sizing) that would otherwise shrink-wrap and centre the sheet;
        // background/color/border come from SHEET_CLASS as before.
        open
        // Same trick as the backdrop — anchor the sheet's bottom edge to
        // the top of the bottom tab bar so the bar stays visible while
        // the sheet is open.
        className={`m-0 p-0 w-auto h-auto ${SHEET_CLASS} ${TABBAR_BOTTOM_CLASS} ${open ? SHEET_OPEN_STATE : SHEET_CLOSED_STATE}`}
        aria-modal="true"
        aria-label={sectionNode ? `${sectionNode.label} menu` : 'Menu'}
      >
        <div className={GRABBER_CLASS} aria-hidden />
        <div className={HEADER_CLASS}>
          <span className={HEADER_TITLE_CLASS}>
            {sectionNode?.label ?? 'Menu'}
          </span>
          <button
            type="button"
            className={HEADER_CLOSE_CLASS}
            onClick={onClose}
            aria-label="Close menu"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden
            >
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className={LIST_CLASS}>
          {sectionNode
            ? renderRows(
                sectionNode.children ?? [],
                0,
                expanded,
                toggleExpanded,
                handleLeafTap,
              )
            : null}
        </div>
      </dialog>
    </>
  );
}

/**
 * Recursive list renderer — leaves emit a navigable button row; parents
 * (`children?.length`) emit an expandable accordion row whose children
 * render at `depth + 1`. Depth caps at the indent class limit (2) since
 * the menu is at most 3 levels deep (`Orders → Sales orders → Amazon`).
 */
function renderRows(
  items: ReadonlyArray<MenuItem>,
  depth: number,
  expanded: ReadonlySet<string>,
  toggleExpanded: (id: string) => void,
  onLeafTap: (leaf: MenuItem) => void,
): JSX.Element[] {
  return items.flatMap((item) =>
    renderItem(item, depth, expanded, toggleExpanded, onLeafTap),
  );
}

/** One item → its row, plus (for an open parent) its children's rows. */
function renderItem(
  item: MenuItem,
  depth: number,
  expanded: ReadonlySet<string>,
  toggleExpanded: (id: string) => void,
  onLeafTap: (leaf: MenuItem) => void,
): JSX.Element[] {
  if (!item.children?.length) {
    return [renderLeafRow(item, depth, onLeafTap)];
  }
  const isOpen = expanded.has(item.id);
  const head = item.route
    ? renderSplitParentRow(item, depth, isOpen, toggleExpanded, onLeafTap)
    : renderPlainParentRow(item, depth, isOpen, toggleExpanded);
  const children = isOpen
    ? renderRows(item.children, depth + 1, expanded, toggleExpanded, onLeafTap)
    : [];
  return [head, ...children];
}

function renderLeafRow(
  item: MenuItem,
  depth: number,
  onLeafTap: (leaf: MenuItem) => void,
): JSX.Element {
  return (
    <button
      key={item.id}
      type="button"
      className={[ROW_BASE_CLASS, ROW_LEAF_CLASS, ROW_INDENT_CLASS(depth)]
        .filter(Boolean)
        .join(' ')}
      onClick={() => onLeafTap(item)}
    >
      <span className={ROW_LABEL_CLASS}>{item.label}</span>
    </button>
  );
}

/**
 * Parent WITH its own route (Amazon, Baldor, CW, GFS, …): split the
 * row. The label navigates to the group's scoped listing
 * (desktop-parity), the chevron toggles the submenu. The previous
 * single-button row did both on one tap — the navigation closed the
 * sheet (which also resets `expanded`), so the submenu could never be
 * seen expanding.
 */
function renderSplitParentRow(
  item: MenuItem,
  depth: number,
  isOpen: boolean,
  toggleExpanded: (id: string) => void,
  onLeafTap: (leaf: MenuItem) => void,
): JSX.Element {
  return (
    <SplitParentRow
      key={item.id}
      item={item}
      depth={depth}
      isOpen={isOpen}
      toggleExpanded={toggleExpanded}
      onLeafTap={onLeafTap}
    />
  );
}

function SplitParentRow({
  item,
  depth,
  isOpen,
  toggleExpanded,
  onLeafTap,
}: Readonly<{
  item: MenuItem;
  depth: number;
  isOpen: boolean;
  toggleExpanded: (id: string) => void;
  onLeafTap: (leaf: MenuItem) => void;
}>) {
  const { t } = useTranslation();
  return (
    <div className={`${SPLIT_ROW_WRAP_CLASS} ${ROW_INDENT_CLASS(depth)}`}>
      <button
        type="button"
        className={SPLIT_LABEL_BTN_CLASS}
        onClick={() => onLeafTap(item)}
      >
        <span className={ROW_LABEL_CLASS}>{item.label}</span>
      </button>
      <button
        type="button"
        className={SPLIT_CHEVRON_BTN_CLASS}
        onClick={() => toggleExpanded(item.id)}
        aria-expanded={isOpen}
        aria-label={t(
          isOpen ? 'menu.collapseItem' : 'menu.expandItem',
          isOpen ? 'Collapse {{label}}' : 'Expand {{label}}',
          { label: item.label },
        )}
      >
        <IconChevronDown
          size={16}
          className={isOpen ? CHEVRON_OPEN_CLASS : CHEVRON_CLOSED_CLASS}
          ariaHidden
        />
      </button>
    </div>
  );
}

/** Routeless parent — the whole row toggles the submenu. */
function renderPlainParentRow(
  item: MenuItem,
  depth: number,
  isOpen: boolean,
  toggleExpanded: (id: string) => void,
): JSX.Element {
  return (
    <button
      key={item.id}
      type="button"
      className={[ROW_BASE_CLASS, ROW_PARENT_CLASS, ROW_INDENT_CLASS(depth)]
        .filter(Boolean)
        .join(' ')}
      onClick={() => toggleExpanded(item.id)}
      aria-expanded={isOpen}
    >
      <span className={ROW_LABEL_CLASS}>{item.label}</span>
      <IconChevronDown
        size={16}
        className={isOpen ? CHEVRON_OPEN_CLASS : CHEVRON_CLOSED_CLASS}
        ariaHidden
      />
    </button>
  );
}

export default MobileMenuSheet;
